import Layout from '@components/layout';
import ImageTextSection from '@components/sections/teaser-text';
import PageTitleSection from '@components/sections/page-title';
import RichTextSection from '@components/sections/rich-text';
import { Seo } from '@components/seo';
import { getNewsList } from '@lib/cms-client';
import { parseDate } from '@lib/date';
import {
  ContainerSize,
  Menu,
  MetaPagination,
  News,
  Settings,
} from '@lib/types';
import { GetServerSidePropsContext } from 'next';
import Pagination from '@components/pagination';
import Container from '@components/container';
import { getNumberFromQueryParam } from '@lib/utils';

type Props = {
  menu: Menu;
  menuFooter: Menu;
  menuFooterSecondary: Menu;
  pagination: MetaPagination;
  settings: Settings;
  news: News[];
  url: string;
};

export default function NewsListPage({
  news,
  menu,
  menuFooter,
  menuFooterSecondary,
  pagination,
  settings,
  url,
}: Props) {
  const title = settings.texts?.newsTitle ?? 'News';

  const renderNoNews = () => (
    <RichTextSection
      data={{
        html: `<p>${
          settings.texts?.noNews ?? 'There are no news currently.'
        }</p>`,
      }}
    />
  );

  return (
    <Layout
      menu={menu}
      menuFooter={menuFooter}
      menuFooterSecondary={menuFooterSecondary}
      settings={settings}>
      <>
        <Seo url={url} title={title} seo={null} />
        <PageTitleSection data={{ title }} />
        {news.length === 0
          ? renderNoNews()
          : news.map((post, index) => (
              <ImageTextSection
                key={post.id}
                data={{
                  ...post,
                  layout: index % 2 > 0 ? 'ImageRight' : 'ImageLeft',
                  subTitle: parseDate(post.publishDate),
                  link: {
                    text: settings.texts?.read ?? 'Read',
                    url: `/news/${post.slug}`,
                  },
                  paragraph: null,
                }}
                fixedHeight
              />
            ))}
        <Container size={ContainerSize.wide}>
          <Pagination {...pagination} />
        </Container>
      </>
    </Layout>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const page = getNumberFromQueryParam(context.query.page, 1);
  const pageSize = getNumberFromQueryParam(context.query.pageSize, 10);

  const data = await getNewsList('en', page, pageSize);
  const {
    news,
    meta: { pagination },
  } = data;

  const url = new URL(
    context.resolvedUrl,
    `http://${context.req.headers.host}`,
  ).toString();

  if (!news) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      url,
      news,
      pagination,
    },
  };
}
